import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Plus } from 'lucide-react'
import { columns } from './columns'
import { DataTable } from './data-table'
import { useIntegrationVendors } from '@/hooks/useIntegrationVendors'
import { Skeleton } from '@/components/ui/skeleton'
import { IntegrationVendorSettingDialog } from './IntegrationVendorSettingDialog'
import { IntegrationVendor } from '@magnetar/data-access'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'

export default function IntegrationVendorsPage() {
  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [search, setSearch] = useState('')
  const [isActive, setIsActive] = useState<boolean | undefined>(undefined)
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [editingVendor, setEditingVendor] = useState<IntegrationVendor | undefined>(undefined)

  const { vendors, pagination, isLoading, isError, mutate } = useIntegrationVendors({
    page,
    pageSize,
    search,
    isActive,
  })

  if (isError) {
    return (
      <div>
        <div className="text-center text-destructive">載入介接廠商列表失敗。請稍後再試。</div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div>
        <Skeleton className="h-8 w-[200px] mb-4" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  // Generate page numbers for pagination
  const generatePaginationItems = () => {
    if (!pagination) return []

    const items = []
    const { page: currentPage, totalPages } = pagination

    // Always show first page
    items.push(1)

    if (currentPage > 3) {
      items.push('ellipsis')
    }

    // Show pages around current page
    for (
      let i = Math.max(2, currentPage - 1);
      i <= Math.min(totalPages - 1, currentPage + 1);
      i++
    ) {
      items.push(i)
    }

    if (currentPage < totalPages - 2) {
      items.push('ellipsis')
    }

    // Always show last page
    if (totalPages > 1) {
      items.push(totalPages)
    }

    return items
  }

  return (
    <div>
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">介接廠商管理</h1>
          <p className="text-muted-foreground mt-2">管理第三方介接廠商的認證資訊和權限</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)}>
          <Plus className="mr-2 h-4 w-4" />
          新增廠商
        </Button>
      </div>

      <div className="h-4" />

      <Card>
        <CardContent className="p-6">
          <DataTable
            columns={columns}
            data={vendors || []}
            onSearch={setSearch}
            onStatusFilter={setIsActive}
            meta={{
              reloadData: () => mutate(),
              onEdit: (vendor: IntegrationVendor) => setEditingVendor(vendor),
            }}
          />

          {pagination && pagination.totalPages > 1 && (
            <div className="mt-4 flex justify-center">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        if (page > 1) setPage(page - 1)
                      }}
                    />
                  </PaginationItem>

                  {generatePaginationItems().map((item, index) => {
                    if (item === 'ellipsis') {
                      return (
                        <PaginationItem key={`ellipsis-${index}`}>
                          <PaginationEllipsis />
                        </PaginationItem>
                      )
                    }

                    return (
                      <PaginationItem key={item}>
                        <PaginationLink
                          href="#"
                          isActive={item === page}
                          onClick={(e) => {
                            e.preventDefault()
                            setPage(item as number)
                          }}
                        >
                          {item}
                        </PaginationLink>
                      </PaginationItem>
                    )
                  })}

                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        if (pagination && page < pagination.totalPages) setPage(page + 1)
                      }}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      </Card>

      <IntegrationVendorSettingDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        onSuccess={() => mutate()}
      />

      <IntegrationVendorSettingDialog
        vendor={editingVendor}
        open={!!editingVendor}
        onOpenChange={(open) => {
          if (!open) {
            setEditingVendor(undefined)
          }
        }}
        onSuccess={() => {
          setEditingVendor(undefined)
          mutate()
        }}
      />
    </div>
  )
}
